import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { usePhoneVerify, VerifyBlock, PhoneVerifyTransport } from "../components/phoneverify";
import styles from "../styles/reportverify.module.css";

/**
 * 학부모 리포트 링크(/consultresult, /dailyreport) 토큰 인증 훅.
 * - ?t=토큰 → peetsunbae /dashboard/reportlink/resolve로 검증. 성공 전에는 리포트 DOM을 그리지 않는다.
 * - 구 파라미터(userId/id, date, pw/code) → 휴대폰 인증 게이트(legacy-verify)로 보내고,
 *   인증 성공 시 서버가 발급한 새 토큰으로 URL을 교체(replaceState) 후 재검증한다.
 * - 만료 토큰(410)도 동일하게 재인증 게이트로 보낸다.
 * 쿠키/세션 없이 토큰만 사용 — 카톡 인앱 브라우저·크로스도메인 제약 대응.
 */

export type ReportLinkStatus = "loading" | "valid" | "legacy-verify" | "invalid";

export type ReportLinkMeta = {
    name: string;
    userId?: number;      // 리포트 내 본인 행 식별용 — 데이터 조회 권한과는 무관 (서버는 토큰 값만 신뢰)
    dateString: string;   // "YYYY-M-D"
    dateNumber: string;   // "YYYYMMDD"
    location?: string;    // daily만 (영문 지점명)
};

// 인증 게이트가 sendcert/checkcert에 실어 보낼 신원 — 만료 토큰이면 {t}, 구 링크면 {linkType, userId, date}
export type LegacyIdentity = { t?: string; linkType?: string; userId?: string; date?: string };

const TOKEN_SHAPE = /^[A-Za-z0-9_-]{20,64}$/;

export function useReportLinkAuth(linkType: "consult" | "daily") {

    const location = useLocation();

    const [status, setStatus] = useState<ReportLinkStatus>("loading");
    const [token, setToken] = useState("");
    const [meta, setMeta] = useState<ReportLinkMeta | null>(null);
    const [legacyIdentity, setLegacyIdentity] = useState<LegacyIdentity | null>(null);

    const resolveToken = (t: string) => {

        setStatus("loading");

        fetch("https://peetsunbae.com/dashboard/reportlink/resolve", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ t }),
        }).then((r) => r.json().then((data) => ({ status: r.status, data }))).then(({ status: httpStatus, data }) => {

            if (data.message === "success") {
                setToken(t);
                setMeta({
                    name: data.name,
                    userId: data.userId,
                    dateString: data.dateString,
                    dateNumber: data.dateNumber,
                    location: data.location,
                });
                setStatus("valid");
                return;
            }

            if (httpStatus === 410) {
                // 만료 토큰 — 같은 신원으로 휴대폰 재인증하면 새 토큰 발급
                setLegacyIdentity({ t });
                setStatus("legacy-verify");
                return;
            }

            setStatus("invalid");

        }).catch(() => {
            setStatus("invalid");
        });
    };

    useEffect(() => {

        const query = new URLSearchParams(location.search);

        var t = query.get("t");

        // consultresult 알림톡은 기존 템플릿 변수를 재활용해 pw 자리에 토큰이 실려 온다
        if (!t && linkType === "consult") {
            const pw = query.get("pw");
            if (pw && TOKEN_SHAPE.test(pw) && !/^\d+$/.test(pw)) {
                t = pw;
            }
        }

        if (t && TOKEN_SHAPE.test(t)) {
            resolveToken(t);
            return;
        }

        // 구 링크 파라미터 — pw/code 값은 신뢰하지 않고 신원(userId, date)만 게이트에 넘긴다
        const legacyUserId = linkType === "consult" ? query.get("userId") : query.get("id");
        const legacyDate = query.get("date");

        if (legacyUserId && /^\d+$/.test(legacyUserId) && legacyDate) {
            setLegacyIdentity({ linkType, userId: legacyUserId, date: legacyDate });
            setStatus("legacy-verify");
            return;
        }

        setStatus("invalid");

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // 휴대폰 인증 성공 → 새 토큰으로 URL 교체 후 재검증 (주소창·공유 URL에서 구 파라미터 제거)
    const onVerified = (newToken?: string) => {
        if (!newToken) {
            return;
        }
        const path = linkType === "consult" ? "/consultresult" : "/dailyreport";
        window.history.replaceState(null, "", `${path}?t=${newToken}`);
        resolveToken(newToken);
    };

    return { status, token, meta, legacyIdentity, onVerified };
}

export type ReportLinkAuth = ReturnType<typeof useReportLinkAuth>;

/* ───────────────────────── 휴대폰 인증 게이트 화면 ───────────────────────── */

const buildTransport = (identity: LegacyIdentity): PhoneVerifyTransport => ({

    requestCert: (d) =>
        fetch("https://peetsunbae.com/dashboard/reportlink/sendcert", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ ...identity, phone: d }),
        }).then((r) => r.json()).then((res) => {
            if (res.message === "success") {
                return { ok: true };
            }
            if (res.message === "PHONE_MISMATCH") {
                return { ok: false, message: "등록된 번호와 일치하지 않습니다. 학원으로 문의해주세요." };
            }
            return { ok: false, message: "인증번호 발송에 실패했습니다. 잠시 후 다시 시도해주세요." };
        }),

    checkCert: (d, code) =>
        fetch("https://peetsunbae.com/dashboard/reportlink/checkcert", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ ...identity, phone: d, certNumber: code }),
        }).then((r) => r.json()).then((res) => {
            if (res.message === "success" && res.t) {
                return { ok: true, t: res.t };
            }
            return { ok: false };
        }),
});

export const ReportVerifyGate: React.FC<{ auth: ReportLinkAuth }> = ({ auth }) => {

    const identity = auth.legacyIdentity || {};
    const v = usePhoneVerify(() => "report-link", buildTransport(identity), auth.onVerified);

    return (
        <div className={styles.gateWrap}>
            <div className={styles.gateCard}>
                <div className={styles.gateTitle}>본인 확인이 필요합니다</div>
                <div className={styles.gateDesc}>
                    리포트 열람을 위해 학원에 등록된<br />
                    학생 또는 학부모님 휴대폰 번호로 인증해주세요.
                </div>
                <VerifyBlock v={v} />
                <div className={styles.gateNote}>
                    인증이 완료되면 리포트가 바로 표시됩니다.
                </div>
            </div>
        </div>
    );
};

export const ReportLinkInvalid: React.FC = () => (
    <div className={styles.gateWrap}>
        <div className={styles.gateCard}>
            <div className={styles.gateTitle}>유효하지 않은 링크입니다</div>
            <div className={styles.gateDesc}>
                링크가 만료되었거나 잘못된 주소입니다.<br />
                카카오톡으로 받으신 최신 링크로 다시 접속해주세요.
            </div>
        </div>
    </div>
);

export const ReportLinkLoading: React.FC = () => (
    <div className={styles.gateWrap}>
        <div className={styles.gateLoading}>불러오는 중…</div>
    </div>
);
